import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, DollarSign, Plus } from "lucide-react";
import { api } from "../../lib/api";

/** Inline "Record savings" panel for the CRM drawer. */
export default function SavingsComposer({ clientId, defaultCreditedTo }) {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_cents: 0, per_founder_cents: {} });
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [credited, setCredited] = useState(defaultCreditedTo || "Kalib");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/admin/vault/${encodeURIComponent(clientId)}/savings`)
      .then(({ data }) => {
        setEntries(data.entries || []);
        setTotals({
          total_cents: data.total_cents || 0,
          per_founder_cents: data.per_founder_cents || {},
        });
      })
      .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const save = async () => {
    const dollars = parseFloat(amount);
    if (isNaN(dollars) || dollars <= 0) {
      toast.error("Enter a positive dollar amount.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/vault/${encodeURIComponent(clientId)}/savings`, {
        amount_cents: Math.round(dollars * 100),
        credited_to: credited,
        note: note.trim() || undefined,
      });
      toast.success(`Logged $${dollars.toLocaleString()} to ${credited}.`);
      setAmount("");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div data-testid="savings-composer" className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/50 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Savings recorded</p>
        <span className="text-xs font-head font-medium text-[#4d5a24] tabular-nums flex items-center gap-0.5">
          <DollarSign className="h-3 w-3" strokeWidth={2} />
          {(totals.total_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>
      {Object.keys(totals.per_founder_cents).length > 0 && (
        <div className="text-[10px] text-[#1e3d3a]/60 mb-3">
          {Object.entries(totals.per_founder_cents).map(([who, cents], i) => (
            <span key={who}>
              {i > 0 && " · "}
              <strong className="text-[#1e3d3a]">{who}</strong> {money(cents)}
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-[1fr_1fr] gap-2 mb-2">
        <input
          data-testid="savings-amount"
          type="number"
          min="0"
          step="1"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount ($)"
          className="text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 focus:border-[#c9a961] focus:outline-none"
        />
        <select
          data-testid="savings-credited"
          value={credited}
          onChange={(e) => setCredited(e.target.value)}
          className="text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 focus:border-[#c9a961] focus:outline-none"
        >
          <option value="Kalib">Kalib</option>
          <option value="John">John</option>
          <option value="team">Team</option>
        </select>
      </div>
      <input
        data-testid="savings-note"
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (e.g. Cancelled duplicate SaaS)"
        className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 mb-2 focus:border-[#c9a961] focus:outline-none"
      />
      <button
        type="button"
        data-testid="savings-log"
        onClick={save}
        disabled={busy || !amount}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        Log savings
      </button>
      {entries.length > 0 && (
        <>
          {/* Timeline — 6-month rolling savings by period, stacked per founder */}
          {(() => {
            // Bucket by YYYY-MM
            const byPeriod = {};
            for (const e of entries) {
              const p = (e.period || (e.created_at || "").slice(0, 7)) || "—";
              const who = e.credited_to || "team";
              byPeriod[p] = byPeriod[p] || { Kalib: 0, John: 0, team: 0 };
              byPeriod[p][who] = (byPeriod[p][who] || 0) + (e.amount_cents || 0);
            }
            const periods = Object.keys(byPeriod).sort().slice(-6);
            const max = Math.max(1, ...periods.map((p) =>
              (byPeriod[p].Kalib || 0) + (byPeriod[p].John || 0) + (byPeriod[p].team || 0),
            ));
            const colors = { Kalib: "#c9a961", John: "#8a4b7a", team: "#4d5a24" };
            return (
              <div className="mt-3 rounded-md bg-white/70 border border-[#1e3d3a]/8 p-2.5" data-testid="savings-timeline">
                <p className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 mb-1.5">Timeline · last {periods.length} periods</p>
                <div className="flex items-end gap-1.5 h-16">
                  {periods.map((p) => {
                    const buckets = byPeriod[p];
                    const total = (buckets.Kalib || 0) + (buckets.John || 0) + (buckets.team || 0);
                    const hK = total ? Math.round(56 * ((buckets.Kalib || 0) / max)) : 0;
                    const hJ = total ? Math.round(56 * ((buckets.John || 0) / max)) : 0;
                    const hT = total ? Math.round(56 * ((buckets.team || 0) / max)) : 0;
                    return (
                      <div key={p} className="flex-1 min-w-0 flex flex-col items-center gap-0.5" title={`${p} · $${(total / 100).toLocaleString()}`}>
                        <div className="w-full flex flex-col-reverse gap-0.5">
                          {hK > 0 && <div style={{ height: hK, background: colors.Kalib }} className="rounded-sm w-full" />}
                          {hJ > 0 && <div style={{ height: hJ, background: colors.John }} className="rounded-sm w-full" />}
                          {hT > 0 && <div style={{ height: hT, background: colors.team }} className="rounded-sm w-full" />}
                        </div>
                        <span className="text-[8px] text-[#1e3d3a]/50 tabular-nums">{p.slice(-5)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-[8.5px] uppercase tracking-[0.18em]">
                  {["Kalib", "John", "team"].map((w) => (
                    <span key={w} className="inline-flex items-center gap-1 text-[#1e3d3a]/55">
                      <span className="h-2 w-2 rounded-sm" style={{ background: colors[w] }} />
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
          <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto text-[11px] text-[#1e3d3a]/70">
          {entries.slice(0, 6).map((e) => (
            <li key={e.id} className="flex items-center gap-2 border-t border-[#1e3d3a]/6 pt-1">
              <span className="tabular-nums font-medium text-[#4d5a24]">{money(e.amount_cents)}</span>
              <span className="text-[9px] uppercase tracking-[0.2em]">{e.credited_to}</span>
              <span className="truncate flex-1">{e.note || e.category}</span>
              <span className="text-[9px] text-[#1e3d3a]/45">{(e.created_at || "").slice(5, 10)}</span>
            </li>
          ))}
        </ul>
        </>
      )}
    </div>
  );
}
